<template>

  <form autocomplete="off">
    <Loader v-if="loading" />
    <div v-else class="box">
      <fieldset>
        <plan-details
          @total-updated="total = $event"
          @count-updated="usersCount = $event"
        />

        <div v-if="subscription.canceled_at && subscription.status !== subscriptionStatus.Trialing" class="mb-6">
          <heading
            v-if="hasNextSubscription"
            class="mb-1 font-semibold"
            :level="1"
          >
            {{ $t('Subscription changed at:') }}
          </heading>
          <heading
            v-else
            class="mb-1 font-semibold"
            :level="1"
          >
            {{ $t('Canceled at') }}
          </heading>
          <p>{{ $du(subscription.canceled_at, 'shortDate') }}</p>
        </div>
        <div v-if="subscription.trial_ends_at" class="mb-6">
          <heading class="mb-1 font-semibold" :level="1">
            {{ $t('Trial ends at') }}
          </heading>
          <p>{{ $du(subscription.trial_ends_at, 'shortDate') }}</p>
        </div>
        <div v-else-if="subscription.ends_at" class="mb-6">
          <heading
            v-if="hasNextSubscription"
            class="mb-1 font-semibold"
            :level="1"
          >
            {{
              $t('Subscription to {nickname} ends at', {
                nickname: subscription.plan.nickname,
              })
            }}
          </heading>
          <heading v-else class="mb-1 font-semibold" :level="1">
            {{ $t('Ends at') }}
          </heading>
          <p>{{ $du(subscription.ends_at, 'shortDate') }}</p>
        </div>
        <div
          v-if="subscription.next_charge_at && !subscription.canceled_at"
          class="mb-6"
        >
          <heading class="mb-1 font-semibold" :level="1">
            {{ $t('Date of next charge') }}
          </heading>
          <p>{{ $du(subscription.next_charge_at, 'shortDate') }}</p>
        </div>
        <div
          v-if="subscription.next_charge_amount && !subscription.canceled_at"
          class="mb-6"
        >
          <heading class="font-semibold" :level="1">
            {{ $t('Amount of next charge') }}
          </heading>
          <small class="text-80">{{ $t('Based on your current configuration') }}</small>
          <p class="mt-2">
            {{
              $n(subscription.next_charge_amount.replace(' ', ''), {
                key: 'currency',
                currencyCode: currentPlan.currency,
              })
            }}
          </p>
        </div>
        <template v-if="hasNextSubscription">
          <div class="mb-6">
            <p class="mt-2">
              {{
                $t('Your billing frequency has been changed successfully. After your {period} subscription ends, the {newPeriod} subscription will automatically begin.', {
                  period: subscription.plan.nickname.toLowerCase(),
                  newPeriod: nextSubscription.plan.nickname.toLowerCase(),
                })
              }}
            </p>
          </div>
        </template>
        <div class="flex justify-start">
          <progress-button
            class=" bg-green mr-2"
            :disabled="updating || currentUserCount > usersCount"
            @click.native="update"
          >
            {{ $t('Save') }}
          </progress-button>
          <progress-button
            v-if="subscription.status !== subscriptionStatus.Trialing"
            class="bg-red btn-secondary"
            :processing="updating"
            :disabled="updating"
            @click.native="handleSubscriptionCancel"
          >
            {{
              subscription.canceled_at
                ? $t('Don\'t cancel')
                : $t('Cancel subscription')
            }}
          </progress-button>
        </div>
      </fieldset>

    </div>
    <transition name="fade">
      <cancel-modal
        v-if="showCancelModal"
        @confirm="unsubscribe"
        @close="closeCancelModal"
      />
    </transition>
  </form>

</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import subscriptionService from '@/_legacy/services/subscription';
  import PlanDetails from '@/_legacy/components/Subscriptions/PlanDetails.vue';
  import CancelModal from '@/_legacy/components/Subscriptions/CancelModal.vue';
  import { du } from '@/ui/i18n/i18n';
  import authMixin from '@/_legacy/mixins/authMixin';
  import { fetchUser } from '@/ui/auth/auth';
  import Loader from '@/_legacy/components/Icons/Loader';
  import { SubscriptionStatus } from '@/entities/subscription/constants';
  import { ProductSlug } from '@/entities/product';

  export default {
    name: 'Details',

    components: {
      CancelModal,
      PlanDetails,
      Loader,
    },

    mixins: [authMixin],

    data() {
      return {
        newProduct: null,
        newPlan: null,
        updating: false,
        usersCount: 0,
        total: 0,
        showCancelModal: false,
        nextSubscription: {},
        loading: true,
        subscriptionStatus: SubscriptionStatus,
      };
    },

    computed: {
      ...mapGetters({
        currentPlan: 'subscription/selectedPlan',
        subscription: 'subscription/subscription',
      }),
      hasNextSubscription() {
        return Object.keys(this.nextSubscription).length;
      },
    },

    watch: {
      subscription() {
        if (this.subscription && this.subscription.next_subscription) {
          this.nextSubscription = this.subscription.next_subscription[0];
        }
      },
    },

    created() {
      //legacy mapping
      this.$du = du;
    },

    async mounted() {
      await this.getPlan({
        slug: this.$route.query.plan,
        interval: this.$route.query.interval,
      });
      await this.fetchPlan({
        slug: ProductSlug.ProfessionalNew,
        interval: this.$route.query.interval ?? 'month'
      }).then(({ product, selected_plan }) => {
        this.newProduct = product;
        this.newPlan = selected_plan;

        this.$store.commit('subscription/setNextProduct', product);
      });
      await this.getSubscription();
      await fetchUser()();
      this.loading = false;
    },

    methods: {
      ...mapActions({
        getSubscription: 'subscription/getSubscription',
        getPlan: 'subscription/getPlan',
        fetchPlan: 'subscription/fetchPlan',
      }),

      unsubscribe() {
        this.showCancelModal = false;
        this.updating = true;
        subscriptionService.unsubscribe()
          .then(() => {
            this.$toasted.show(
              this.$t('Success'),
              { type: 'success' },
            );
            this.$router.push({ name: 'subscription' });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          })
          .catch(e => {
            this.$toasted.show(
              this.$t('Failed'),
              { type: 'error' },
            );
            this.updating = false;
          });
      },

      update() {
        this.updating = true;
        subscriptionService.updateSubscription(this.currentPlan, this.usersCount)
          .then(() => {
            this.$toasted.show(
              this.$t('Successfully updated!'),
              { type: 'success' },
            );
            this.updating = false;
          })
          .catch(e => {
            this.$toasted.show(
              this.$t(e.response.data.message),
              { type: 'error' },
            );
            this.updating = false;
          });
      },

      handleSubscriptionCancel() {
        if (this.subscription.canceled_at) {
          this.unsubscribe();
          return;
        }
        this.showCancelModal = true;
      },

      closeCancelModal() {
        this.showCancelModal = false;
      },
    },
  };
</script>
